import React, { Component } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import styled, { ThemeProvider } from 'styled-components';
import { Helmet } from 'react-helmet';

import theme from '../constants/theme';

import {
  formatLocation,
  generateURLPath,
  getCountryFromArea,
  SEOTypeToType,
  typeToReadableSEOType,
} from '../helpers';

import SearchActions from '../actions/search';

import { Container, ContainerInner } from './Container';
import Footer from './Footer';
import Header from './Header';
import Loading from './Loading';
import SearchForm from './SearchForm';
import FilterByTags from './FilterByTags';
import {
  AdditionalOptionsWrapper,
  AdditionalOption,
} from './SearchAdditionalOptionsWrapper';
import { VendorCard } from '@sayido/ui';

const Results = styled.div`
  align-items: start;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 30px;
  padding: 45px 0;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    grid-gap: 45px;
    padding: 16px;
  }
`;

const NoResult = styled.p`
  color: #666;
  margin: 1rem 0 0 0;
  max-width: 220px;
  font-size: 1rem;
  line-height: 1.15rem;
  margin-left: 1.33em;
`;

class SearchResultsWrapper extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  static defaultProps = {
    profile: {},
  };

  static requestInitialData(routeData) {
    const { areaLevel1, areaLevel2, tag } = routeData.params;
    const type = SEOTypeToType(routeData.params.type);

    return SearchActions.performSearch({
      ...(type && { type: type }),
      ...(areaLevel1 && { area_level_1: areaLevel1 }),
      ...(areaLevel2 && { area_level_2: areaLevel2 }),
      ...(tag && { tags: tag }),
    });
  }

  performSearch = filters => {
    const { area_level_1, area_level_2, type, tag } = filters;
    const { dispatch } = this.props;

    return dispatch(
      SearchActions.performSearch({
        ...(type && { type: type }),
        ...(area_level_1 && { area_level_1: area_level_1 }),
        ...(area_level_2 && { area_level_2: area_level_2 }),
        ...(tag && { tags: tag }),
      })
    );
  };

  render() {
    const { search, type, area_level_1, area_level_2, tag } = this.props;

    const { results, facets, loading } = search;
    const country = getCountryFromArea(area_level_1, area_level_2);
    const readable = {
      type: typeToReadableSEOType(type, 1),
      country: formatLocation(country),
      level1: formatLocation(country, area_level_1),
      level2: area_level_2 ? formatLocation(country, area_level_2) : null,
    };
    const readableLocation = `${readable.level2 ? `${readable.level2}, ` : ''}${
      readable.level1 ? readable.level1 : 'your area'
    }`;
    const metaTitle = `Wedding ${readable.type} in ${readableLocation}`;
    const metaDescription = `Find the best Wedding ${readable.type} in ${readableLocation} on Say I do, with prices, photos and services. Find and get in touch with wedding professionals in your area.`;

    const narrowOptions =
      type !== 'transport-and-cars' && // Don't show for transport, because we want to show specific vehicles instead
      (!area_level_2 || area_level_2 === 'all') &&
      facets &&
      facets.area_level_2 &&
      Object.keys(facets.area_level_2).length &&
      Object.keys(facets.area_level_2).filter(o => facets.area_level_2[o] > 1);

    // TODO: Combine this into the FilterByTags component since it is largely the same thing
    const narrowExpandSection = () => {
      if (narrowOptions && narrowOptions.length) {
        return (
          <AdditionalOptionsWrapper>
            <h4>Narrow your search</h4>
            <div>
              {narrowOptions.map((location, index) => (
                <AdditionalOption
                  key={index}
                  href={generateURLPath({
                    type,
                    area_level_1,
                    area_level_2: location,
                  })}
                >
                  {formatLocation(country, location)}
                </AdditionalOption>
              ))}
            </div>
          </AdditionalOptionsWrapper>
        );
      }
      if (!narrowOptions && area_level_2 !== 'all') {
        return (
          <AdditionalOptionsWrapper>
            <h4>Expand your search</h4>
            <div>
              <AdditionalOption
                href={generateURLPath({
                  type,
                  area_level_1,
                })}
              >
                All {formatLocation(country, area_level_1)}
              </AdditionalOption>
            </div>
          </AdditionalOptionsWrapper>
        );
      }
      return null;
    };

    return (
      <ThemeProvider theme={theme}>
        <Container>
          <Header />
          <ContainerInner>
            <SearchForm
              searchFn={this.performSearch.bind(this)}
              filters={{ type, area_level_1, area_level_2 }}
              country="au"
            />

            {narrowExpandSection()}

            {facets && facets.tags && (
              <FilterByTags
                tags={facets.tags}
                type={type}
                country={country}
                area_level_1={area_level_1}
                currentFilter={tag}
              />
            )}

            {loading ? (
              <Loading />
            ) : (
              <Results>
                {results?.length ? (
                  <>
                    {results.map((result, index) => (
                      <VendorCard vendor={result} key={index} />
                    ))}
                  </>
                ) : (
                  <NoResult>No vendors found</NoResult>
                )}
              </Results>
            )}

            {narrowExpandSection()}

            {facets && facets.tags && (
              <FilterByTags
                tags={facets.tags}
                type={type}
                country={country}
                area_level_1={area_level_1}
                currentFilter={tag}
              />
            )}
          </ContainerInner>
          <Footer />
        </Container>
        <Helmet>
          <meta property="og:site_name" content="sayi.do"></meta>
          <meta property="og:type" data-rh="true" content="article" />
          <meta property="og:title" content={metaTitle}></meta>
          <meta property="og:description" content={metaDescription} />
          <meta name="description" content={metaDescription}></meta>
          {/* 
          <meta
            property="og:image"
            content={
              _.has(vendor, "contact_image.original")
                ? vendor.contact_image.original
                : ""
            }
          /> */}
          <title>{metaTitle} | Say I do</title>
        </Helmet>
      </ThemeProvider>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
  };
}

function mapStateToProps(state) {
  return {
    search: state.search,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchResultsWrapper);
